import { BrowserRouter, Navigate, Outlet, Route, Routes, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./AuthContext";
import { GameProvider } from "./GameContext";
import { GameLayout } from "./GameLayout";
import { LoginPage } from "../pages/LoginPage";
import { RegisterPage } from "../pages/RegisterPage";
import { OverviewPage } from "../pages/OverviewPage";
import { StarMapPage } from "../pages/StarMapPage";
import { ResourcesPage } from "../pages/ResourcesPage";
import { TechnologyPage } from "../pages/TechnologyPage";
import { FleetPage } from "../pages/FleetPage";
import { DiplomacyPage } from "../pages/DiplomacyPage";
import { EventsPage } from "../pages/EventsPage";
import { SettingsPage } from "../pages/SettingsPage";
import { SaveLobbyPage } from "../pages/SaveLobbyPage";
import { StellarProvider } from "../stellar-app/StellarContext";
import { StellarLayout } from "../stellar-app/StellarLayout";
import {
  StellarArchivePage,
  StellarCorePage,
  StellarSettingsPage,
} from "../stellar-app/StellarPages";

const aliases = ["star-map", "resources", "technology", "fleet", "diplomacy", "events", "settings"] as const;

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route element={<RequireAuth />}>
            <Route path="/game" element={<SaveLobbyPage />} />
            <Route path="/play/:saveId" element={<SaveGameRoute />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<OverviewPage />} />
              <Route path="star-map" element={<StarMapPage />} />
              <Route path="resources" element={<ResourcesPage />} />
              <Route path="technology" element={<TechnologyPage />} />
              <Route path="development" element={<Navigate to="../star-map" replace />} />
              <Route path="fleet" element={<FleetPage />} />
              <Route path="diplomacy" element={<DiplomacyPage />} />
              <Route path="events" element={<EventsPage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>
            <Route path="/stellar" element={<StellarRoute />}>
              <Route index element={<StellarCorePage />} />
              <Route path="archive" element={<StellarArchivePage />} />
              <Route path="settings" element={<StellarSettingsPage />} />
              <Route path="*" element={<Navigate to="/stellar" replace />} />
            </Route>
          </Route>
          {aliases.map((path) => <Route key={path} path={`/${path}`} element={<Navigate to="/game" replace />} />)}
          <Route path="/development" element={<Navigate to="/game" replace />} />
          <Route path="/game/*" element={<Navigate to="/game" replace />} />
          <Route path="/" element={<HomeRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

function SaveGameRoute() {
  const { saveId = "" } = useParams<{ saveId: string }>();
  return <GameProvider key={saveId}><GameLayout /></GameProvider>;
}

function StellarRoute() {
  return <StellarProvider><StellarLayout /></StellarProvider>;
}

function RequireAuth() {
  const auth = useAuth();
  if (auth.status === "loading") return <main className="splash" role="status">正在建立安全会话…</main>;
  if (auth.status === "error") return (
    <main className="splash">
      <h1>无法连接</h1><p>{auth.error}</p><button className="button primary" onClick={() => void auth.refresh()}>重试</button>
    </main>
  );
  return auth.user ? <Outlet /> : <Navigate to="/login" replace />;
}

function HomeRedirect() {
  const auth = useAuth();
  if (auth.status === "loading") return <main className="splash" role="status">正在建立安全会话…</main>;
  return <Navigate to={auth.user ? "/game" : "/login"} replace />;
}
